/**
 * App footer with branding, links, and decorative treeline
 * Story 8.7: Footer with Nordic treeline silhouette
 * Story 9.16: Increased link contrast for accessibility
 *
 * Design notes (from mood-story-sagamoon.md):
 * - Should ground the page like the bottom of a book
 * - Desktop: Includes treeline silhouette
 * - Mobile: Keep minimal to preserve space
 *
 * @module components/layout/Footer
 */

import { Link } from 'react-router-dom';
import { useTranslation } from '../../locales';
import { TreelineSilhouette } from '../decorative';

/**
 * Footer props
 */
interface FooterProps {
  /**
   * Optional subject line for the "Ota yhteyttä" mailto link.
   * When set, it is appended as a URL-encoded `?subject=`. The public
   * PublicLayout (S20.1) passes a Finnish enquiry subject; the authenticated
   * AppLayout passes nothing, preserving the plain mailto behaviour.
   */
  contactSubject?: string;
  /**
   * Story 20.20: show the "Tilaa uutiskirje" newsletter link. Only the
   * authenticated AppLayout passes this; the public PublicLayout does NOT
   * (the newsletter is an in-service feature, not a public sagamoon.com one).
   */
  showNewsletterLink?: boolean;
}

/**
 * Footer component
 * Provides consistent footer across all protected pages
 *
 * Story 8.7: Uses twilight theme colors with treeline decoration
 * Story 20.1: Optional contactSubject prop for the public-site footer
 */
export function Footer({ contactSubject, showNewsletterLink }: FooterProps = {}): JSX.Element {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const contactHref = contactSubject
    ? `mailto:support@sagamoon.com?subject=${encodeURIComponent(contactSubject)}`
    : 'mailto:support@sagamoon.com';

  return (
    <footer className="mt-auto relative">
      {/* Treeline silhouette - desktop only, positioned behind content */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <TreelineSilhouette color="#1a1528" />
      </div>

      {/* Footer content - text on twilight gradient background */}
      <div className="relative z-10 py-6 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-center justify-center space-y-2 text-center">
            {/* Brand */}
            <p className="text-sm text-text-twilight-cream/80">© {currentYear} Sagamoon</p>

            {/* Links - Story 9.16: Increased contrast from 60% to 75% opacity */}
            {/* Story 13.14: in-app links use <Link> for SPA navigation; the
                mailto link stays as <a> because it's not a route. */}
            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-text-twilight-cream/75">
              <Link to="/about" className="hover:text-text-twilight-cream transition-fast">
                {t('common.footer.aboutUs')}
              </Link>
              <span className="hidden sm:inline text-text-twilight-cream/50">•</span>
              <Link to="/help" className="hover:text-text-twilight-cream transition-fast">
                {t('common.footer.help')}
              </Link>
              <span className="hidden sm:inline text-text-twilight-cream/50">•</span>
              <Link to="/privacy" className="hover:text-text-twilight-cream transition-fast">
                {t('common.footer.privacy')}
              </Link>
              <span className="hidden sm:inline text-text-twilight-cream/50">•</span>
              <Link to="/terms" className="hover:text-text-twilight-cream transition-fast">
                {t('common.footer.terms')}
              </Link>
              <span className="hidden sm:inline text-text-twilight-cream/50">•</span>
              <a href={contactHref} className="hover:text-text-twilight-cream transition-fast">
                {t('common.footer.contact')}
              </a>
              {/* Story 20.20: newsletter link — authenticated footer only */}
              {showNewsletterLink && (
                <>
                  <span className="hidden sm:inline text-text-twilight-cream/50">•</span>
                  <Link to="/uutiskirje" className="hover:text-text-twilight-cream transition-fast">
                    {t('newsletter.footerLink')}
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
